import { TESTIMONIALS_META } from '../constants/home'
import { ORDER_UI } from '../constants/orders'
import { SKELETON_COUNTS, type CountedSkeletonProps, type ProductGridSkeletonProps, type SkeletonProps } from '../constants/skeleton'
import { WISHLIST_UI } from '../constants/wishlist'
import { cx } from '../helpers/format'
import { range } from '../helpers/range'
import '../styles/skeleton.scss'

export function Skeleton({ className }: SkeletonProps) {
  return <span className={cx('skeleton', className)} aria-hidden />
}

export function HeroSkeleton() {
  return (
    <section className="hero" aria-busy="true" aria-label="Loading promotions">
      <div className="hero__frame">
        <Skeleton className="skeleton--hero" />
      </div>
    </section>
  )
}

export function CategoryTilesSkeleton() {
  return (
    <section className="section category-tiles" aria-busy="true" aria-label="Loading categories">
      <div className="container">
        <div className="category-tiles__grid">
          {range(SKELETON_COUNTS.categoryTiles).map((index) => (
            <div key={index} className="skeleton-category-tile">
              <Skeleton className="skeleton--tile-media" />
              <Skeleton className="skeleton--tile-label" />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function PromoBannerSkeleton() {
  return (
    <section className="section home-promo" aria-busy="true" aria-label="Loading promotions">
      <div className="home-promo__frame">
        <Skeleton className="skeleton--promo-banner" />
      </div>
    </section>
  )
}

export function ProductGridSkeleton({
  count = SKELETON_COUNTS.products,
}: ProductGridSkeletonProps) {
  return (
    <div className="product-grid" aria-busy="true" aria-label="Loading products">
      {range(count).map((index) => (
        <div key={index} className="skeleton-product-card">
          <Skeleton className="skeleton--product-media" />
          <div className="skeleton-product-card__body">
            <Skeleton className="skeleton--line skeleton--line-lg" />
            <Skeleton className="skeleton--line skeleton--line-sm" />
            <Skeleton className="skeleton--line skeleton--line-md" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function PartnersSkeleton() {
  return (
    <section className="section partners" aria-busy="true" aria-label="Loading partner logos">
      <div className="container">
        <div className="section__head">
          <Skeleton className="skeleton--heading skeleton--partners-title" />
        </div>
        <div className="partners__viewport">
          <div className="partners__group partners__group--skeleton">
            {range(SKELETON_COUNTS.partnerLogos).map((index) => (
              <div key={index} className="partners__item">
                <Skeleton className="skeleton--partner-logo" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export function BlogTeaserSkeleton() {
  return (
    <div className="blog-teaser__grid" aria-busy="true" aria-label="Loading articles">
      {range(SKELETON_COUNTS.blogTeaser).map((index) => (
        <div key={index} className="skeleton-blog-card">
          <Skeleton className="skeleton--blog-media" />
          <div className="skeleton-blog-card__body">
            <Skeleton className="skeleton--line skeleton--line-sm" />
            <Skeleton className="skeleton--line skeleton--line-lg" />
            <Skeleton className="skeleton--line skeleton--line-full" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function BlogListSkeleton() {
  return (
    <div className="blog-list" aria-busy="true" aria-label="Loading articles">
      {range(SKELETON_COUNTS.blogList).map((index) => (
        <div key={index} className="skeleton-blog-card skeleton-blog-card--list">
          <Skeleton className="skeleton--blog-media" />
          <div className="skeleton-blog-card__body">
            <Skeleton className="skeleton--line skeleton--line-sm" />
            <Skeleton className="skeleton--line skeleton--line-lg" />
            <Skeleton className="skeleton--line skeleton--line-full" />
            <Skeleton className="skeleton--line skeleton--line-md" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function BlogDetailSkeleton() {
  return (
    <div className="blog-page" aria-busy="true" aria-label="Loading article">
      <div className="container skeleton-blog-detail">
        <Skeleton className="skeleton--line skeleton--line-sm" />
        <Skeleton className="skeleton--heading skeleton--heading-xl" />
        <Skeleton className="skeleton--line skeleton--line-md" />
        <Skeleton className="skeleton--blog-cover" />
        <Skeleton className="skeleton--line skeleton--line-full" />
        <Skeleton className="skeleton--line skeleton--line-full" />
        <Skeleton className="skeleton--line skeleton--line-lg" />
        <Skeleton className="skeleton--line skeleton--line-full" />
        <Skeleton className="skeleton--line skeleton--line-md" />
      </div>
    </div>
  )
}

export function ProductPageSkeleton() {
  return (
    <div className="product-page" aria-busy="true" aria-label="Loading product">
      <div className="container">
        <div className="skeleton-breadcrumbs">
          <Skeleton className="skeleton--line skeleton--line-sm" />
          <Skeleton className="skeleton--line skeleton--line-sm" />
        </div>
        <div className="product-layout">
          <div className="product-media">
            <Skeleton className="skeleton--gallery" />
            <div className="skeleton-thumbs">
              {range(SKELETON_COUNTS.productThumbs).map((index) => (
                <Skeleton key={index} className="skeleton--thumb" />
              ))}
            </div>
          </div>
          <div className="skeleton-product-details">
            <Skeleton className="skeleton--line skeleton--line-sm" />
            <Skeleton className="skeleton--heading skeleton--heading-xl" />
            <Skeleton className="skeleton--line skeleton--line-lg" />
            <Skeleton className="skeleton--price" />
            <div className="skeleton-options">
              {range(SKELETON_COUNTS.productOptions).map((index) => (
                <Skeleton key={index} className="skeleton--option" />
              ))}
            </div>
            <Skeleton className="skeleton--cta" />
          </div>
        </div>
      </div>
    </div>
  )
}

export function OrderListSkeleton({
  count = SKELETON_COUNTS.orders,
}: CountedSkeletonProps) {
  return (
    <div className="order-cards" aria-busy="true" aria-label={ORDER_UI.loadingAria}>
      {range(count).map((index) => (
        <div key={index} className="order-card skeleton-order-card">
          <div className="order-card__top">
            <div className="skeleton-order-card__copy">
              <Skeleton className="skeleton--line skeleton--line-md" />
              <Skeleton className="skeleton--line skeleton--line-sm" />
            </div>
            <Skeleton className="skeleton--status" />
          </div>
          <Skeleton className="skeleton--line skeleton--line-full" />
          <div className="order-card__footer">
            <Skeleton className="skeleton--line skeleton--line-md" />
            <Skeleton className="skeleton--line skeleton--line-sm" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function OrderDetailSkeleton() {
  return (
    <article className="order-detail" aria-busy="true" aria-label={ORDER_UI.loadingAria}>
      <header className="order-detail__head">
        <div className="skeleton-order-card__copy">
          <Skeleton className="skeleton--heading" />
          <Skeleton className="skeleton--line skeleton--line-sm" />
        </div>
        <Skeleton className="skeleton--status" />
      </header>
      <div className="skeleton-order-items">
        {range(SKELETON_COUNTS.orderItems).map((index) => (
          <div key={index} className="skeleton-order-item">
            <Skeleton className="skeleton--thumb" />
            <div className="skeleton-order-card__copy">
              <Skeleton className="skeleton--line skeleton--line-lg" />
              <Skeleton className="skeleton--line skeleton--line-sm" />
            </div>
            <Skeleton className="skeleton--line skeleton--line-sm" />
          </div>
        ))}
      </div>
      <Skeleton className="skeleton--line skeleton--line-full" />
      <Skeleton className="skeleton--line skeleton--line-md" />
      <Skeleton className="skeleton--line skeleton--line-lg" />
    </article>
  )
}

export function WishlistListSkeleton({
  count = SKELETON_COUNTS.wishlist,
}: CountedSkeletonProps) {
  return (
    <ul className="wishlist-lines" aria-busy="true" aria-label={WISHLIST_UI.loadingAria}>
      {range(count).map((index) => (
        <li key={index} className="wishlist-line">
          <Skeleton className="skeleton--wishlist-media" />
          <div className="wishlist-line__body">
            <Skeleton className="skeleton--line skeleton--line-lg" />
            <Skeleton className="skeleton--line skeleton--line-sm" />
            <Skeleton className="skeleton--line skeleton--line-md" />
          </div>
        </li>
      ))}
    </ul>
  )
}

export function TestimonialsSkeleton() {
  return (
    <section className="section testimonials" aria-busy="true" aria-label={TESTIMONIALS_META.loadingAria}>
      <div className="container">
        <div className="section__head section__head--center">
          <div>
            <h2>{TESTIMONIALS_META.title}</h2>
            <p className="section__sub">{TESTIMONIALS_META.subtitle}</p>
          </div>
        </div>
        <div className="skeleton-testimonials">
          {range(SKELETON_COUNTS.testimonials).map((index) => (
            <div key={index} className="testimonial-card skeleton-testimonial-card">
              <Skeleton className="skeleton--line skeleton--line-sm" />
              <Skeleton className="skeleton--line skeleton--line-full" />
              <Skeleton className="skeleton--line skeleton--line-full" />
              <Skeleton className="skeleton--line skeleton--line-md" />
              <div className="testimonial-card__footer">
                <Skeleton className="skeleton--avatar" />
                <div className="skeleton-order-card__copy">
                  <Skeleton className="skeleton--line skeleton--line-sm" />
                  <Skeleton className="skeleton--line skeleton--line-md" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
